import { Skeleton } from '@/components/ui/app-skeleton';
import { FEED_CARD_RESTING_OUTLINE } from '@/lib/ui/feed-card-chrome';
import { cn } from '@/lib/ui/utils';

interface LeaderboardSkeletonProps {
  /** Number of metric columns the active tab will render. Sizes the header +
   *  numeric placeholders so the skeleton matches the final layout (PnL=4,
   *  Boosters/Backers/Creators=3). Defaults to 3. */
  columnCount?: number;
}

// Mirrors the live leaderboard `DataTable` (feed chrome, spec 093): rank `w-8`,
// the identity column, then right-aligned `%`-width metric columns. Each row is
// a bordered card (`bg-background-primary` + `FEED_CARD_RESTING_OUTLINE`, the
// SAME chrome the real rows use) with `gap-2` between them — matching the
// table's `border-spacing-y-2`. For the 4-column board the last two metrics are
// `lg`-only, exactly like the live Trades / Markets columns. Below `md` the
// table collapses to the card stack (rank + avatar + identity + primary value).

/** Per-metric-column class. On the 4-column board the trailing pair is
 *  `lg`-only (matches the live `hidden lg:table-cell` Trades / Markets). */
function metricColClass(index: number, columnCount: number): string {
  const lgOnly: boolean = columnCount >= 4 && index >= 2;
  return cn('shrink-0 text-right', lgOnly ? 'hidden lg:block lg:w-[13%]' : 'w-[24%] lg:w-[18%]');
}

export function LeaderboardSkeleton({
  columnCount = 3,
}: LeaderboardSkeletonProps): React.ReactNode {
  const metricKeys: readonly number[] = Array.from({ length: columnCount }, (_, i) => i);

  return (
    <div className="flex flex-col gap-2">
      {/* Desktop (>= md): header rhythm + feed card rows. */}
      <div className="hidden md:flex md:flex-col md:gap-2">
        {/* Column header — bottom hairline, tertiary labels (matches the live feed header). */}
        <div className="flex items-center border-b border-border-primary px-4 pb-2 pt-4">
          <div className="w-8 shrink-0" />
          <div className="min-w-0 flex-1">
            <Skeleton className="h-3.5 w-16" />
          </div>
          {metricKeys.map((i) => (
            <div key={`col-${String(i)}`} className={metricColClass(i, columnCount)}>
              <Skeleton className="ml-auto h-3.5 w-12" />
            </div>
          ))}
        </div>

        {Array.from({ length: 8 }, (_, i) => (
          <div
            key={`row-${String(i)}`}
            className={cn(
              'flex min-h-14 items-center rounded-card bg-background-primary px-4 py-4',
              FEED_CARD_RESTING_OUTLINE,
            )}
          >
            <div className="flex w-8 shrink-0 justify-center">
              <Skeleton className="size-6 rounded-full" />
            </div>
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <Skeleton className="size-10 shrink-0 rounded-full" />
              <div className="flex min-w-0 flex-col gap-1.5">
                <Skeleton className="h-3.5 w-24" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
            {metricKeys.map((colIdx) => (
              <div
                key={`row-${String(i)}-col-${String(colIdx)}`}
                className={metricColClass(colIdx, columnCount)}
              >
                <Skeleton className="ml-auto h-3.5 w-14" />
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Mobile (< md): rank + avatar + identity left, primary value right — feed cards. */}
      <div className="flex flex-col gap-2 md:hidden">
        {Array.from({ length: 6 }, (_, i) => (
          <div
            key={`mobile-row-${String(i)}`}
            className={cn(
              'flex items-center gap-3 rounded-card bg-background-primary px-4 py-4',
              FEED_CARD_RESTING_OUTLINE,
            )}
          >
            <Skeleton className="size-6 shrink-0 rounded-full" />
            <Skeleton className="size-10 shrink-0 rounded-full" />
            <div className="flex min-w-0 flex-col gap-1.5">
              <Skeleton className="h-3.5 w-24" />
              <Skeleton className="h-3 w-16" />
            </div>
            <Skeleton className="ml-auto h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}
